import React from 'react';
import SecuritySection from './ui/SecuritySection';
import SecurityStatusPill from './ui/SecurityStatusPill';
import SecuritySeverityBadge from './ui/SecuritySeverityBadge';
import SecurityEmptyState from './ui/SecurityEmptyState';
import { IconServer } from '../../ui/adminIcons';

function formatUptime(seconds = 0) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

function DiagnosticsCard({ label, children }) {
  return (
    <div className="vx-sec-diag-card">
      <span className="vx-sec-diag-label">{label}</span>
      <div className="vx-sec-diag-value">{children}</div>
    </div>
  );
}

export default function SecurityDiagnosticsPanel({
  healthData,
  diagnosticsData,
  lastUpdatedAt,
  loading,
}) {
  const dbConnected = healthData?.database?.connected;
  const issues = diagnosticsData?.issues || [];
  const securityScore = diagnosticsData?.securityScore;
  const email = healthData?.email || {};
  const geo = healthData?.security?.geoProtection || {};

  return (
    <SecuritySection title="System diagnostics" icon={<IconServer />} id="security-diagnostics">
      {!loading && dbConnected === false ? (
        <div className="vx-sec-diag-alert" role="alert">
          Production database is disconnected. Security event logging and geographic aggregates may be unavailable.
        </div>
      ) : null}

      {loading && !healthData ? (
        <SecurityEmptyState title="Loading diagnostics…" />
      ) : (
        <>
          <div className="vx-sec-diag-grid">
            <DiagnosticsCard label="MongoDB">
              <SecurityStatusPill
                active={dbConnected}
                activeLabel="Connected"
                inactiveLabel="Disconnected"
              />
            </DiagnosticsCard>

            <DiagnosticsCard label="Security monitoring">
              <SecurityStatusPill
                active={healthData?.security?.monitoringEnabled}
                activeLabel="Active"
                inactiveLabel="Disabled"
              />
            </DiagnosticsCard>

            <DiagnosticsCard label="Security score">
              {typeof securityScore === 'number' ? `${securityScore.toFixed(1)}/10` : '—'}
            </DiagnosticsCard>

            <DiagnosticsCard label="API uptime">
              {formatUptime(healthData?.application?.uptime)}
            </DiagnosticsCard>

            <DiagnosticsCard label="Environment">
              {healthData?.application?.environment || '—'}
            </DiagnosticsCard>

            <DiagnosticsCard label="Blocked IPs">
              {healthData?.security?.blockedIPs ?? '—'}
            </DiagnosticsCard>

            <DiagnosticsCard label="Suspicious IPs">
              {healthData?.security?.suspiciousIPs ?? '—'}
            </DiagnosticsCard>

            <DiagnosticsCard label="Geo protection">
              {geo.blockedCountries ?? 0} countries blocked
            </DiagnosticsCard>

            <DiagnosticsCard label="Email provider">
              {email.provider || '—'}
            </DiagnosticsCard>

            <DiagnosticsCard label="SendGrid">
              <SecurityStatusPill
                active={email.sendgridConfigured}
                activeLabel="Configured"
                inactiveLabel="Not configured"
              />
            </DiagnosticsCard>

            <DiagnosticsCard label="AWS SES">
              <SecurityStatusPill
                active={email.sesConfigured}
                activeLabel="Configured"
                inactiveLabel="Not configured"
              />
            </DiagnosticsCard>

            <DiagnosticsCard label="Gmail SMTP">
              <SecurityStatusPill
                active={email.gmailConfigured}
                activeLabel="Configured"
                inactiveLabel="Not configured"
              />
            </DiagnosticsCard>

            <DiagnosticsCard label="Last dashboard refresh">
              {lastUpdatedAt ? new Date(lastUpdatedAt).toLocaleString() : '—'}
            </DiagnosticsCard>

            <DiagnosticsCard label="Memory (heap)">
              {healthData?.application?.memory?.heapUsedMb != null
                ? `${healthData.application.memory.heapUsedMb} MB`
                : '—'}
            </DiagnosticsCard>
          </div>

          {issues.length > 0 ? (
            <div className="vx-sec-diag-issues">
              <h3 className="vx-sec-subtitle">Active issues ({issues.length})</h3>
              <ul className="vx-sec-diag-issue-list">
                {issues.map((issue) => (
                  <li key={`${issue.type}-${issue.title}`} className="vx-sec-diag-issue">
                    <div className="vx-sec-diag-issue-head">
                      <SecuritySeverityBadge severity={issue.type} />
                      <strong>{issue.title}</strong>
                    </div>
                    <p>{issue.description}</p>
                    {issue.action ? <p className="vx-sec-muted">Recommended: {issue.action}</p> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <SecurityEmptyState title="No active diagnostic issues detected" />
          )}
        </>
      )}
    </SecuritySection>
  );
}
